import { memo, useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ShadowSpinner } from "../Loader/ShadowSpinner";
import { VideoPlayer } from "../VideoPlayer/VideoPlayer";
import { LiveBadgeIcon, PlayIcon } from "../Icons/MediaIcons";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { useFireworkPlayerEvents } from "../../hooks/useFireworkPlayerEvents";
import { PipOverlayLabel } from "./PipOverlayLabel";
import { thumbnailLoader } from "../../utils/image";
import {
  clearBodyTransformReset,
  releaseHtmlNoScroll,
  computeEffectiveIsLive,
  getHashtagsForWeb,
  getUnifiedMediaCardThumbnail,
  isEventForPlayer,
  pausePlayerByEncodedId,
  pausePlayerByName,
  playMutedPreview,
  stopPreview,
  timeSinceEnded as utilTimeSinceEnded,
  triggerPlayerFullscreen,
} from "../../utils/mediaUtils";
import type { UnifiedMediaCardProps } from "../../types/livelanding.types";

const videoDescriptionMap: Record<string, string> = {};

function activateSharedPlayer(
  encodedId: string | undefined,
  setIsPlayerActive: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (!encodedId) return;

  setIsPlayerActive(true);
  triggerPlayerFullscreen(`player-${encodedId}`);
}

function renderMediaStatusBadge({
  endedAt,
  videoEndedAt,
  effectiveIsLive,
  endedAtTimeString,
  status,
}: {
  endedAt: string | null | undefined;
  videoEndedAt: string | null | undefined;
  effectiveIsLive: boolean;
  endedAtTimeString: string;
  status: string | null | undefined;
}) {
  const hasEndedAt = Boolean(endedAt) || Boolean(videoEndedAt);
  if (hasEndedAt) {
    if (effectiveIsLive) return null;
    return (
      <span className="font-rubik rounded-[2px] bg-[#1A1A1AB2] !px-[5px] !py-[2px] !text-[10px] font-medium text-white sm:!px-[4px] sm:!py-[1px] sm:!text-[10px] md:!px-[6px] md:!py-[1px] md:!text-[12px] xl:!px-[5px] xl:!py-[1px] xl:!text-[14px]">
        {endedAtTimeString}
      </span>
    );
  }

  if (status === "active") {
    return (
      <span className="mr-[11px] flex items-center gap-1 rounded-sm bg-[#ff0000] !px-1.5 py-0.5 text-[10px] font-medium text-white uppercase md:px-0 md:!text-[14px]">
        <LiveBadgeIcon />
        LIVE
      </span>
    );
  }

  return null;
}

function handleThumbnailKeyDown(
  event: React.KeyboardEvent<HTMLButtonElement>,
  encodedId: string | undefined,
  setIsPlayerActive: React.Dispatch<React.SetStateAction<boolean>>,
  onThumbnailClick: UnifiedMediaCardProps["onThumbnailClick"]
) {
  if (!encodedId) return;

  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    activateSharedPlayer(encodedId, setIsPlayerActive);
    onThumbnailClick?.(encodedId);
  }
}

function usePlayerLifecycle(encodedId: string | undefined) {
  useEffect(() => {
    const handleTransition = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const transitionTo = detail?.transitionTo ?? detail?.to ?? detail?.state;

      if (transitionTo === "default") {
        const eventPlayerName =
          detail?.playerName ??
          detail?.player_name ??
          detail?.name ??
          detail?.id ??
          detail?.playerId;
        if (eventPlayerName) {
          pausePlayerByName(eventPlayerName);
        } else {
          pausePlayerByEncodedId(encodedId, 0);
        }
        return;
      }

      if (transitionTo === "minimized") {
        releaseHtmlNoScroll();
      }
    };

    const handleQuit = (e: Event) => {
      pausePlayerByEncodedId(encodedId, 0);
      clearBodyTransformReset();
    };

    document.addEventListener("fw:player:transition", handleTransition);
    document.addEventListener("fw:player:quit", handleQuit);

    return () => {
      document.removeEventListener("fw:player:transition", handleTransition);
      document.removeEventListener("fw:player:quit", handleQuit);
    };
  }, [encodedId]);
}

function useMediaIntersectionObserver(
  variant: UnifiedMediaCardProps["variant"],
  videoRef: React.RefObject<HTMLVideoElement | null>
) {
  useEffect(() => {
    if (variant !== "media") return;
    if (globalThis.window === undefined || typeof IntersectionObserver === "undefined") return;

    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          try {
            observer.unobserve(videoElement);
          } catch {
            // ignore
          }
        }
      });
    });

    try {
      observer.observe(videoElement);
    } catch {
      // ignore
    }

    return () => {
      try {
        observer.disconnect();
      } catch {
        // ignore
      }
    };
  }, [variant, videoRef]);
}

function formatViews(count: number) {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return `${count}`;
}

function querySelectorIncludingOpenShadowRoots(
  selector: string,
  root: ParentNode = document
): Element | null {
  const directMatch = root.querySelector(selector);
  if (directMatch) return directMatch;

  const elements = root.querySelectorAll("*");
  for (const element of elements) {
    const shadowRoot = (element as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot;
    if (!shadowRoot) continue;

    const nestedMatch = querySelectorIncludingOpenShadowRoots(selector, shadowRoot);
    if (nestedMatch) return nestedMatch;
  }

  return null;
}

function findStoryBlockElement(encodedId: string | undefined) {
  const cardRoot = encodedId ? document.getElementById(encodedId) : null;

  if (cardRoot) {
    const cardScopedSelectors = [
      `fw-storyblock[name="player-${encodedId}"]`,
      `fw-storyblock[video="${encodedId}"]`,
      'fw-storyblock[data-testid="video-player"]',
      "fw-storyblock",
    ];

    for (const selector of cardScopedSelectors) {
      const found = querySelectorIncludingOpenShadowRoots(selector, cardRoot) as
        (HTMLElement & { shadowRoot?: ShadowRoot }) | null;
      if (found) return found;
    }
  }

  const selectors = [
    ...(encodedId
      ? [`fw-storyblock[name="player-${encodedId}"]`, `fw-storyblock[video="${encodedId}"]`]
      : []),
    'fw-storyblock[data-testid="video-player"]',
    "fw-storyblock",
  ];

  for (const selector of selectors) {
    const found = querySelectorIncludingOpenShadowRoots(selector) as
      (HTMLElement & { shadowRoot?: ShadowRoot }) | null;
    if (found) return found;
  }

  return null;
}

function setStoryBlockPipMessageZIndex(encodedId: string | undefined, maxRetries = 8) {
  const trySetZIndex = (attempt: number) => {
    const storyBlock = findStoryBlockElement(encodedId);
    const pipMessage = storyBlock?.shadowRoot?.querySelector(
      ".cy-video-in-pip-message"
    ) as HTMLElement | null;

    if (!pipMessage?.parentElement) {
      if (attempt < maxRetries) {
        requestAnimationFrame(() => {
          trySetZIndex(attempt + 1);
        });
      }
      return;
    }

    pipMessage.parentElement.style.zIndex = "4";
  };

  trySetZIndex(0);
}

function getOverlayOpacityClass(pipThumbnailOverlayHidden: boolean, isCardHovered: boolean) {
  if (pipThumbnailOverlayHidden) return "opacity-100";
  return isCardHovered ? "opacity-0" : "opacity-100";
}

function renderWebVariantCard({
  sectionId,
  encodedId,
  pipThumbnailOverlayHidden,
  thumbnail,
  video,
  viewsCount,
  effectiveIsLive,
  timeSinceEndedWeb,
  hashtagsForWeb,
  isPlayerActive,
  isLoading,
  isCardHovered,
  videoRef,
  handleMouseEnter,
  handleMouseLeave,
  handleClick,
  handleKeyDown,
}: {
  sectionId: UnifiedMediaCardProps["sectionId"];
  encodedId?: string;
  pipThumbnailOverlayHidden: boolean;
  thumbnail: string;
  video: UnifiedMediaCardProps["video"];
  viewsCount: number;
  effectiveIsLive: boolean;
  timeSinceEndedWeb: UnifiedMediaCardProps["timeSinceEndedWeb"];
  hashtagsForWeb: ReturnType<typeof getHashtagsForWeb>;
  isPlayerActive: boolean;
  isLoading: boolean;
  isCardHovered: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}) {
  const overlayId = `${sectionId}-overlay-${encodedId}`;
  const trailerId = `${sectionId}-trailer-${encodedId}`;

  const animatedPoster = video.trailer?.video_posters?.find(
    (p: { video_poster_type: string }) => p.video_poster_type === "animated"
  )?.url;
  const overlayOpacityClass = getOverlayOpacityClass(pipThumbnailOverlayHidden, isCardHovered);

  return (
    <button
      type="button"
      id={encodedId}
      className="font-inherit group flex-[0_0_210px] cursor-pointer overflow-hidden rounded-lg border-0 bg-white p-0 text-left transition-transform duration-300 max-[1200px]:flex-[0_0_180px] max-[768px]:flex-[0_0_120px] max-[420px]:flex-[0_0_109px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid="video-card"
    >
      <div className="relative aspect-[9/16.3] max-h-[380px] max-w-[214px] overflow-hidden rounded-lg bg-black max-[1200px]:max-h-[320px] max-[1200px]:max-w-[180px] max-[420px]:max-h-[300px] max-[420px]:max-w-[160px] max-md:max-h-[300px] max-md:max-w-[160px] xl:max-h-[380px] xl:max-w-[214px]">
        <div className="absolute top-0 right-0 left-0 z-3 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent !p-[4px] lg:!p-[8px]">
          {effectiveIsLive ? (
            <span className="mr-[11px] flex items-center gap-1 rounded-[2px] bg-[#ff0000] px-1 py-0.5 !text-[10px] font-medium text-white uppercase md:!text-[14px]">
              <LiveBadgeIcon />
              LIVE
            </span>
          ) : (
            <span className="font-rubik rounded-[2px] bg-[#1A1A1AB2] !px-[5px] !py-[2px] text-[10px] font-medium text-white sm:!px-[6px] sm:!py-[2px] sm:!text-[11px] md:!px-[6px] md:!py-[2px] md:!text-[12px] xl:!px-[6px] xl:!py-[2px] xl:!text-[14px]">
              {(timeSinceEndedWeb ?? utilTimeSinceEnded)(video?.scheduled_at ?? null)}
            </span>
          )}
          {viewsCount > 0 && (
            <span className="flex items-center gap-1 rounded-[2px] bg-[#1A1A1AB2] px-[6px] py-[2px] text-[10px] font-medium text-white md:text-[12px] xl:text-[14px]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5C7 5 3 9 2 12c1 3 5 7 10 7s9-4 10-7c-1-3-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                  fill="white"
                />
              </svg>
              {formatViews(viewsCount)}
            </span>
          )}
        </div>

        <div className="absolute top-0 left-0 h-full w-full">
          {animatedPoster && !pipThumbnailOverlayHidden ? (
            <img
              className="absolute top-0 left-0 z-[1] h-full w-full object-contain"
              src={animatedPoster}
              alt={video.event_name ? `${video.event_name} preview` : "Video preview"}
            />
          ) : (
            <video
              ref={videoRef}
              className={`absolute top-1/2 left-1/2 z-[1] h-[380px] w-[214px] -translate-x-1/2 -translate-y-1/2 object-cover ${pipThumbnailOverlayHidden ? "hidden" : ""}`}
              src={video.trailer?.file_url ?? ""}
              id={trailerId}
              loop
              playsInline
              muted
              preload="metadata"
            >
              <track kind="captions" src="" srcLang="en" label="English" />
            </video>
          )}
          {isPlayerActive ? (
            <VideoPlayer
              className="h-full w-full"
              videoId={video.trailer?.encoded_id ?? ""}
              autoplay={false}
              maxVideos={1}
              playerCaptions={true}
              productCard={false}
            />
          ) : null}
        </div>

        {isLoading && (
          <div className="absolute inset-0 z-[10] flex items-center justify-center bg-black/50">
            <ShadowSpinner ariaLabel="Loading video" />
          </div>
        )}

        <div
          className={`absolute top-0 left-0 h-full w-full bg-[rgba(0,0,0,0.2)] transition-opacity duration-300 ${overlayOpacityClass}`}
          id={overlayId}
          style={{ zIndex: 2 }}
        >
          <Image
            src={thumbnail}
            alt={video.event_name || "Video"}
            fill
            loader={thumbnailLoader}
            className="z-[0] object-cover"
            sizes="(max-width: 420px) 160px, (max-width: 768px) 180px, 214px"
            quality={70}
            priority={false}
          />
          <div
            className={`absolute top-1/2 left-1/2 flex h-[42px] w-[42px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 transition-all duration-300 ease-in-out max-[768px]:!h-[28px] max-[768px]:!w-[28px]`}
          >
            <PlayIcon className="ml-[3px] h-[30px] w-[30px]" />
          </div>
        </div>

        {pipThumbnailOverlayHidden && <PipOverlayLabel />}
      </div>

      <div className="pt-[6px]">
        <h3 className="font-rubik ml-[3px] line-clamp-2 !text-[12px] leading-[1.4] font-normal text-[#1A1A1A] sm:!text-[12px] md:!text-[16px] xl:!text-[16px]">
          {video?.event_name}
        </h3>
        <div className="font-rubik ml-[4px] inline-block !text-[12px] font-normal text-[#999999] sm:!text-[12px] md:!text-[14px] xl:!text-[14px]">
          {<span className="mr-2">{video.event_description}</span>}
        </div>
      </div>
      {encodedId && <span style={{ display: "none" }}>{encodedId}</span>}
    </button>
  );
}

function UnifiedMediaCardComponent({
  video,
  onMouseEnter,
  onMouseLeave,
  onThumbnailClick,
  variant,
  sectionId,
  isLive,
  timeSinceEndedWeb,
  isAllTab = false,
  activeCategory,
  language = "en",
  ended_at,
  size,
  liveOverride,
  textColor = "dark",
}: Readonly<UnifiedMediaCardProps>) {
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlayerActive, setIsPlayerActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [pipThumbnailOverlayHidden, setPipThumbnailOverlayHidden] = useState(false);
  const encodedId = video?.trailer?.encoded_id;
  const videoDescription = video?.event_description || video?.trailer?.description || "NA";

  useEffect(() => {
    const handleFwProductClick = (event: Event) => {
      const detail = (event as CustomEvent).detail;

      const clickedVideoId = detail?.video?.encoded_id || detail?.name?.replace("player-", "");

      //  only track THIS card's video
      if (clickedVideoId !== encodedId) return;

      const videoId = clickedVideoId || "";

      //  1. capture FW description (same as StoryBlock)
      const fwDescription = detail?.video?.description;

      //  2. store it in map (important)
      if (videoId && fwDescription) {
        videoDescriptionMap[videoId] = fwDescription;
      }

      //  3. use exact same fallback chain as StoryBlock
      const productName =
        fwDescription ||
        videoDescriptionMap[videoId] ||
        video?.trailer?.description ||
        video?.event_description ||
        "NA";
      const productTitle = detail?.product?.title || detail?.product?.product_name || "View Offers";
      const productId = detail?.product_id || detail?.product?.id || "NA";

      const cleverTapObject = {
        EVENT_NAME: "LIVE_COMMERCE_PAGE_CLICKED",
        EP_COMPONENT_NAME: "explore_top_products",
        EP_CLICK_TYPE: "video_card_button_click",
        EP_REFERENCE_ID: "explore_top_products",
        EP_PROPERTY_TYPE: "clp",

        EP_SECTION: "Explore Top Products",
        EP_CTA: "PRODUCT_CLICK",
        EP_CTA_TEXT: "PRODUCT_CLICK",

        EP_PAGE_NAME: "live-page",
        EP_EVENT_TYPE: "click",

        EP_JOURNEY: "LIVE_LANDING",
        EP_JOURNEY_NAME: "LIVE_LANDING",

        EP_PRODUCT_NAME: productName,
        EP_PRODUCT_TITLE: productTitle,
        EP_PRODUCT_ID: productId,

        EP_PRODUCT_CATEGORY: `${productName} || ${encodedId}`,

        EP_DESTINATION: detail?.product?.custom_cta_url || detail?.product?.url || "NA",
      };

      dataLayer("", {}, cleverTapObject);
    };

    const events = ["fw:shopping:product-clicked"];
    events.forEach((e) => document.addEventListener(e, handleFwProductClick));

    return () => {
      events.forEach((e) => document.removeEventListener(e, handleFwProductClick));
    };
  }, [encodedId, videoDescription]);

  const closeLoader = useCallback(() => setIsLoading(false), []);

  const shouldHandlePlayerEvent = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent).detail;
      return isEventForPlayer(detail, encodedId);
    },
    [encodedId]
  );

  const handlePlayerTransition = useCallback(
    (event: Event) => {
      if (!shouldHandlePlayerEvent(event)) return;
      const detail = (event as CustomEvent).detail;
      const transitionTo = detail?.transitionTo ?? detail?.to ?? detail?.state;

      const normalizedTransition =
        typeof transitionTo === "string" ? transitionTo.trim().toLowerCase() : "";
      if (normalizedTransition === "minimized" || normalizedTransition === "minimized state") {
        setPipThumbnailOverlayHidden(true);
      }

      if (normalizedTransition === "default" || normalizedTransition === "fullscreen") {
        setPipThumbnailOverlayHidden(false);
      }

      if (!transitionTo || transitionTo !== "opening") {
        closeLoader();
      }
    },
    [closeLoader, encodedId, shouldHandlePlayerEvent]
  );

  const handlePlayerQuit = useCallback(
    (event: Event) => {
      if (!shouldHandlePlayerEvent(event)) return;
      setPipThumbnailOverlayHidden(false);
      closeLoader();
    },
    [closeLoader, shouldHandlePlayerEvent]
  );

  useFireworkPlayerEvents({
    enabled: true,
    onTransition: handlePlayerTransition,
    onQuit: handlePlayerQuit,
  });

  const thumbnail = getUnifiedMediaCardThumbnail({ video, variant });

  const effectiveIsLive = computeEffectiveIsLive({
    isLiveProp: isLive,
    liveOverride,
    startedAt: video?.started_at ?? null,
    endedAt: video?.ended_at ?? null,
  });

  const endedAtTimeString = utilTimeSinceEnded(ended_at ?? video?.ended_at ?? null);

  const viewsCount = Number(video?.trailer?.views_count ?? 0);

  const rawHashtags = video.trailer?.hashtags ?? [];
  const hashtagsForWeb = getHashtagsForWeb({
    rawHashtags,
    isAllTab,
    activeCategory,
    language,
  });

  const handleMouseEnter = () => {
    setIsCardHovered(true);
    playMutedPreview(previewVideoRef.current);
    if (encodedId) onMouseEnter?.(encodedId);
  };
  const handleMouseLeave = () => {
    setIsCardHovered(false);
    stopPreview(previewVideoRef.current);
    if (encodedId) onMouseLeave?.(encodedId);
  };
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isPlayerActive) {
      if (encodedId) {
        setIsLoading(true);
        triggerPlayerFullscreen(`player-${encodedId}`);
      }
      return;
    }
    stopPreview(previewVideoRef.current);
    if (encodedId) {
      setIsLoading(true);
      activateSharedPlayer(encodedId, setIsPlayerActive);

      onThumbnailClick?.(encodedId, {
        productName: videoDescription,
        productCategory: `${videoDescription} || ${encodedId}`,
      });
    }
  };
  usePlayerLifecycle(encodedId);
  useMediaIntersectionObserver(variant, previewVideoRef);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isPlayerActive) {
      if ((event.key === "Enter" || event.key === " ") && encodedId) {
        event.preventDefault();
        setIsLoading(true);
        triggerPlayerFullscreen(`player-${encodedId}`);
      }
      return;
    }
    handleThumbnailKeyDown(event, encodedId, setIsPlayerActive, (id) => {
      onThumbnailClick?.(id, {
        productName: videoDescription,
        productCategory: `${videoDescription} || ${id}`,
      });
    });
  };

  if (variant === "web") {
    return renderWebVariantCard({
      sectionId,
      encodedId,
      thumbnail,
      video,
      viewsCount,
      effectiveIsLive,
      timeSinceEndedWeb,
      hashtagsForWeb,
      isPlayerActive,
      isLoading,
      isCardHovered,
      pipThumbnailOverlayHidden,
      videoRef: previewVideoRef,
      handleMouseEnter,
      handleMouseLeave,
      handleClick,
      handleKeyDown,
    });
  }

  const animatedPoster = video.trailer?.video_posters?.find(
    (p: { video_poster_type: string }) => p.video_poster_type === "animated"
  )?.url;
  const overlayOpacityClass = getOverlayOpacityClass(pipThumbnailOverlayHidden, isCardHovered);

  return (
    <div
      id={encodedId}
      className={`!max-w-[112px] flex-[0_0_250px] sm:!max-w-[140px] md:!max-w-[190px] md:flex-[0_0_300px] lg:!max-w-[200px] xl:!max-w-[210px] ${size ?? ""}`}
    >
      <button
        type="button"
        className="font-inherit group z-[1] h-full w-full cursor-pointer overflow-hidden border-0 bg-transparent p-0 text-left"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <div className="relative aspect-[9/15.8] w-full overflow-hidden rounded-lg bg-black sm:aspect-[9/16.3]">
          <div className="absolute top-0 right-0 left-0 z-[3] flex items-center justify-between bg-gradient-to-b from-[rgba(0,0,0,0.6)] to-transparent p-1 sm:p-1 lg:p-2">
            {/* LEFT: days ago / LIVE */}
            {renderMediaStatusBadge({
              endedAt: ended_at,
              videoEndedAt: video?.ended_at,
              effectiveIsLive,
              endedAtTimeString,
              status: video?.status,
            })}

            {/* RIGHT: views */}
            {viewsCount > 0 && (
              <span className="flex items-center gap-1 rounded-[2px] bg-[#1A1A1AB2] px-[6px] py-[2px] text-[10px] font-medium text-white md:text-[12px]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5C7 5 3 9 2 12c1 3 5 7 10 7s9-4 10-7c-1-3-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                    fill="white"
                  />
                </svg>
                {formatViews(viewsCount)}
              </span>
            )}
          </div>
          <div className="absolute top-0 left-0 h-full w-full">
            {animatedPoster && !pipThumbnailOverlayHidden ? (
              <img
                className="absolute top-0 left-0 z-[1] h-full w-full object-cover"
                src={animatedPoster}
                alt={video.event_name ? `${video.event_name} preview` : "Video preview"}
              />
            ) : (
              <video
                ref={previewVideoRef}
                className={`absolute top-0 left-0 z-[1] h-full w-full object-cover ${pipThumbnailOverlayHidden ? "hidden" : ""}`}
                src={video.trailer?.file_url}
                loop
                playsInline
                muted
                preload="metadata"
                poster={thumbnail}
              >
                {video.trailer?.captions_url ? (
                  <track
                    kind="captions"
                    src={video.trailer.captions_url}
                    srcLang="en"
                    label="English"
                  />
                ) : null}
              </video>
            )}
            {isPlayerActive ? (
              <VideoPlayer
                className="h-full w-full"
                videoId={video.trailer?.encoded_id ?? ""}
                autoplay={false}
                maxVideos={1}
                playerCaptions={true}
                productCard={false}
              />
            ) : null}
          </div>
          {isLoading && (
            <div className="absolute inset-0 z-[10] flex items-center justify-center bg-black/50">
              <ShadowSpinner ariaLabel="Loading video" />
            </div>
          )}
          <div
            className={`absolute inset-0 transition-opacity duration-300 ease-in-out ${overlayOpacityClass}`}
            style={{ zIndex: 2 }}
          >
            <div className="absolute inset-0 bg-black/20">
              <Image
                src={thumbnail}
                alt={video.event_name || "Video"}
                fill
                loader={thumbnailLoader}
                className="object-cover"
                sizes="(max-width: 420px) 112px, (max-width: 768px) 140px, (max-width: 1024px) 190px, 210px"
                quality={70}
                priority={false}
              />
            </div>
            <div
              className={`absolute top-1/2 left-1/2 flex h-[42px] w-[42px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 transition-all duration-300 ease-in-out max-[768px]:!h-[28px] max-[768px]:!w-[28px]`}
            >
              <PlayIcon className="ml-[3px] h-[30px] w-[30px]" />
            </div>
          </div>

          {pipThumbnailOverlayHidden && <PipOverlayLabel />}
        </div>
      </button>
      <div>
        <h3
          className={`line-clamp-2 overflow-hidden pt-[6px] font-['Rubik'] text-[16px] leading-[1.4] font-normal ${
            textColor === "light" ? "text-white" : "text-[#1A1A1A]"
          } max-[1200px]:text-[16px] max-[768px]:text-[14px] max-[420px]:text-[12px]`}
        >
          {video.event_name}
        </h3>
        {video.event_description && (
          <div
            className={`-ml-[2px] inline-block px-[2px] py-[2px] font-[Rubik] text-[14px] font-normal ${
              textColor === "light" ? "text-[#CCCCCC]" : "text-[#999999]"
            } max-[420px]:text-[12px] md:text-[14px] xl:text-[14px]`}
          >
            {video.event_description}
          </div>
        )}
      </div>
      {encodedId && <span style={{ display: "none" }}>{encodedId}</span>}
    </div>
  );
}

function getCardVideoKey(video: UnifiedMediaCardProps["video"]): string {
  const trailer = video?.trailer;
  return [
    video?.id,
    video?.encoded_id,
    trailer?.encoded_id,
    video?.status,
    video?.started_at,
    video?.ended_at,
    video?.event_name,
    video?.event_description,
    trailer?.views_count,
    trailer?.file_url,
    trailer?.thumbnail_url,
    trailer?.captions_url,
  ]
    .map((value) => String(value ?? ""))
    .join("|");
}

function areUnifiedMediaCardPropsEqual(
  prev: Readonly<UnifiedMediaCardProps>,
  next: Readonly<UnifiedMediaCardProps>
): boolean {
  return (
    getCardVideoKey(prev.video) === getCardVideoKey(next.video) &&
    prev.variant === next.variant &&
    prev.sectionId === next.sectionId &&
    prev.isLive === next.isLive &&
    prev.timeSinceEndedWeb === next.timeSinceEndedWeb &&
    prev.isAllTab === next.isAllTab &&
    prev.activeCategory === next.activeCategory &&
    prev.language === next.language &&
    prev.ended_at === next.ended_at &&
    prev.size === next.size &&
    prev.liveOverride === next.liveOverride &&
    prev.textColor === next.textColor
  );
}

export const UnifiedMediaCard = memo(UnifiedMediaCardComponent, areUnifiedMediaCardPropsEqual);
